import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import { formApi } from "@/lib/api";
import { siteSettings } from "@/content/site";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BookOpen, CheckCircle } from "@phosphor-icons/react";

const POINTS = [
  "quali errori evitare",
  "cosa chiedere prima di iniziare",
  "quali informazioni servono per un preventivo serio",
  "perché il prezzo da solo non basta",
  "come capire se un lavoro è organizzato bene",
  "cosa può creare ritardi o costi extra",
];

export default function Guida() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", comune: "", quando: "", privacy: false });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.phone || !form.privacy) {
      toast.error("Qualcosa non ha funzionato. Controlla i dati inseriti o riprova tra qualche minuto.");
      return;
    }
    setSubmitting(true);
    try {
      await formApi.createGuidaLead({
        first_name: form.first_name,
        last_name: form.last_name,
        name: `${form.first_name} ${form.last_name}`.trim(),
        email: form.email,
        phone: form.phone,
        comune: form.comune,
        quando: form.quando,
        privacy_consent: form.privacy,
      });
      navigate("/grazie-guida");
    } catch {
      toast.error("Qualcosa non ha funzionato. Controlla i dati inseriti o riprova tra qualche minuto.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-warm-white min-h-screen">
      <Navbar />
      <section className="section-pad">
        <div className="max-w-6xl mx-auto px-5 md:px-10 grid md:grid-cols-2 gap-12 items-start">
          <div>
            <div className="overline mb-4">Guida gratuita</div>
            <h1 className="font-display text-4xl md:text-5xl leading-tight mb-5">Scarica la guida gratuita prima di rifare il bagno.</h1>
            <p className="text-base md:text-lg text-[#5C5C59] leading-relaxed mb-5">Una guida semplice per capire cosa controllare prima di iniziare una ristrutturazione bagno.</p>
            <p className="text-[#5C5C59] leading-relaxed mb-6">Prima di scegliere un'impresa o accettare un preventivo, ci sono alcuni aspetti che dovresti valutare con attenzione.</p>
            <div className="flex items-center gap-3 text-sm text-oasi-green mb-6">
              <BookOpen size={20} /> La guida Oasi Bagni ti aiuta a capire:
            </div>
            <ul className="space-y-2 mb-8">
              {POINTS.map((p) => (
                <li key={p} className="flex gap-2 text-sm">
                  <CheckCircle size={16} weight="fill" className="text-oasi-green mt-0.5 flex-shrink-0" /> {p}
                </li>
              ))}
            </ul>

            <div className="relative w-[260px] h-[330px] card-soft overflow-hidden rotate-[-3deg] hidden md:block">
              <div className="absolute inset-0 bg-sand flex items-center">
                <div className="w-[32%] bg-white h-full p-4 flex flex-col justify-between border-r border-brand">
                  <div className="logo-mark text-[11px] leading-[0.9]">OASI<br />BAGNI</div>
                  <div className="text-[8px] text-[#5C5C59] leading-tight">La guida per partire bene senza stress</div>
                </div>
                <div className="flex-1 pr-5 pl-3">
                  <div className="text-[9px] uppercase tracking-[0.2em] text-white/80 mb-3">Guida gratuita</div>
                  <div className="font-display text-white text-3xl leading-[0.95] font-semibold">BAGNO<br />SERENO<br />SUBITO</div>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="card-soft p-6 md:p-8 space-y-3" data-testid="guida-form">
            <div className="overline mb-2">Scarica la guida gratuita Oasi Bagni</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Input placeholder="Nome *" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="h-12" data-testid="guida-name" />
              <Input placeholder="Cognome *" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="h-12" />
            </div>
            <Input type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12" data-testid="guida-email" />
            <Input placeholder="Telefono *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-12" data-testid="guida-phone" />
            <Input placeholder="Comune" value={form.comune} onChange={(e) => setForm({ ...form, comune: e.target.value })} className="h-12" />
            <Select value={form.quando} onValueChange={(v) => setForm({ ...form, quando: v })}>
              <SelectTrigger className="h-12"><SelectValue placeholder="Quando pensi di rifare il bagno?" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Subito">Subito</SelectItem>
                <SelectItem value="Entro 1 mese">Entro 1 mese</SelectItem>
                <SelectItem value="Entro 3 mesi">Entro 3 mesi</SelectItem>
                <SelectItem value="Sto solo valutando">Sto solo valutando</SelectItem>
              </SelectContent>
            </Select>
            <label className="flex items-start gap-3 text-sm text-[#5C5C59] pt-1">
              <Checkbox checked={form.privacy} onCheckedChange={(v) => setForm({ ...form, privacy: !!v })} data-testid="guida-privacy" />
              <span>Ho letto l'<Link to="/privacy" className="underline">informativa privacy</Link>. Chiedo di essere ricontattato in merito alla mia richiesta.</span>
            </label>
            <button type="submit" disabled={submitting} className="btn-primary w-full py-3.5 rounded-full font-medium disabled:opacity-60" data-testid="guida-submit">
              {submitting ? "Invio..." : "Voglio ricevere la guida"}
            </button>
            <p className="text-xs text-[#5C5C59] italic text-center">Ti invieremo la guida e potrai valutare con più consapevolezza il tuo progetto.</p>
          </form>
        </div>
      </section>
      <Footer settings={siteSettings} />
      <WhatsAppFloat number={siteSettings.whatsapp_number} />
    </div>
  );
}
